import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import { FiBarChart2 } from 'react-icons/fi';
import PropTypes from 'prop-types';

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && error.error) return error.error;
  return 'An error occurred';
};

const COLORS = { p_count: '#F59E0B', j_count: '#3B82F6' };

const LEGEND = [
  { key: 'p_count', label: 'Number of P', color: COLORS.p_count },
  { key: 'j_count', label: 'Number of J', color: COLORS.j_count }
];

const HtmlTooltip = ({ visible, x, y, item }) => {
  if (!visible || !item) return null;
  return (
    <div
      style={{
        position: 'fixed',
        left: x + 14,
        top: y - 10,
        zIndex: 9999,
        pointerEvents: 'none',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        padding: '10px 14px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
        minWidth: '155px'
      }}
    >
      <p className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-2 tw-border-b tw-border-gray-100 tw-pb-1 tw-truncate">
        {item.cse_name || 'Unknown'}
      </p>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-1">
        <span className="tw-text-xs tw-text-gray-600 tw-font-semibold">Total CO#</span>
        <span className="tw-text-xs tw-font-bold tw-text-gray-900">{item.total_change_orders || 0}</span>
      </div>
      {LEGEND.map(s => (
        <div key={s.key} className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-1">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-inline-block tw-w-2.5 tw-h-2.5 tw-rounded-sm" style={{ backgroundColor: s.color }} />
            <span className="tw-text-xs tw-text-gray-600">{s.label}</span>
          </div>
          <span className="tw-text-xs tw-font-bold" style={{ color: s.color }}>{item[s.key] || 0}</span>
        </div>
      ))}
      <div className="tw-mt-2 tw-pt-1 tw-border-t tw-border-gray-100 tw-flex tw-justify-between">
        <span className="tw-text-xs tw-text-gray-500">Projects</span>
        <span className="tw-text-xs tw-font-bold tw-text-gray-900">{item.total_projects || 0}</span>
      </div>
    </div>
  );
};

const cseItemShape = PropTypes.shape({
  cse_name:            PropTypes.string,
  p_count:             PropTypes.number,
  j_count:             PropTypes.number,
  total_change_orders: PropTypes.number,
  total_projects:      PropTypes.number
});

HtmlTooltip.propTypes = {
  visible: PropTypes.bool,
  x:       PropTypes.number,
  y:       PropTypes.number,
  item:    cseItemShape
};

const StackedBarChart = ({ data }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, item: null });

  if (!data || data.length === 0) return null;

  const chartData = data.slice(0, 30);

  const minColW = 52;
  const height  = 340;
  const pad     = { top: 40, right: 24, bottom: 68, left: 54 };
  const plotH   = height - pad.top - pad.bottom;
  const plotW   = Math.max(chartData.length * minColW, 600);
  const width   = plotW + pad.left + pad.right;

  const maxVal  = Math.max(...chartData.map(d => d.total_change_orders || 0));
  const yMax    = maxVal > 0 ? Math.ceil(maxVal * 1.2) : 10;
  const yScale  = plotH / yMax;
  const colW    = plotW / chartData.length;
  const barW    = Math.min(colW * 0.55, 44);
  const yTicks  = 5;

  return (
    <>
      <HtmlTooltip {...tooltip} />
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ minWidth: width }}>
        {/* Grid + Y labels */}
        {Array.from({ length: yTicks + 1 }).map((_, i) => {
          const val = Math.round((i / yTicks) * yMax);
          const y   = height - pad.bottom - (i / yTicks) * plotH;
          return (
            <React.Fragment key={i}>
              <line x1={pad.left} y1={y} x2={width - pad.right} y2={y}
                stroke="#e5e7eb" strokeWidth="1" strokeDasharray={i === 0 ? '0' : '4'} />
              <text x={pad.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">{val}</text>
            </React.Fragment>
          );
        })}

        {/* Y axis */}
        <line x1={pad.left} y1={pad.top} x2={pad.left} y2={height - pad.bottom}
          stroke="#d1d5db" strokeWidth="1.5" />

        {/* Legend */}
        <g transform={`translate(${pad.left}, 12)`}>
          {LEGEND.map((s, i) => (
            <g key={s.key} transform={`translate(${i * 110}, 0)`}>
              <rect x="0" y="0" width="11" height="11" fill={s.color} rx="2" />
              <text x="15" y="9" fontSize="11" fill="#374151" fontWeight="500">{s.label}</text>
            </g>
          ))}
        </g>

        {chartData.map((item, gi) => {
          const cx    = pad.left + gi * colW + colW / 2;
          const bx    = cx - barW / 2;
          const base  = height - pad.bottom;
          const total = item.total_change_orders || 0;
          const pVal  = item.p_count || 0;
          const jVal  = item.j_count || 0;

          const jH = jVal * yScale;
          const pH = pVal * yScale;
          const jY = base - jH - pH;
          const pY = base - pH;

          const label = (item.cse_name || 'N/A').substring(0, 4).toUpperCase();

          return (
            <g
              key={gi}
              onMouseMove={(e) => setTooltip({ visible: true, x: e.clientX, y: e.clientY, item })}
              onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, item: null })}
              style={{ cursor: 'pointer' }}
            >
              <rect x={pad.left + gi * colW} y={pad.top} width={colW} height={plotH} fill="transparent" />

              {/* J segment (top, blue) */}
              {jVal > 0 && (
                <rect x={bx} y={jY} width={barW} height={Math.max(jH, 2)}
                  fill={COLORS.j_count} rx="3" opacity="0.9" />
              )}
              {/* P segment (bottom, amber) */}
              {pVal > 0 && (
                <rect x={bx} y={pY} width={barW} height={Math.max(pH, 2)}
                  fill={COLORS.p_count} rx={jVal > 0 ? '0' : '3'} opacity="0.9" />
              )}

              {/* Segment labels */}
              {jH > 16 && (
                <text x={cx} y={jY + jH / 2 + 4} textAnchor="middle"
                  fontSize="10" fontWeight="700" fill="white" pointerEvents="none">{jVal}</text>
              )}
              {pH > 16 && (
                <text x={cx} y={pY + pH / 2 + 4} textAnchor="middle"
                  fontSize="10" fontWeight="700" fill="white" pointerEvents="none">{pVal}</text>
              )}

              {/* Total above bar */}
              {total > 0 && (
                <text x={cx} y={jY - 5} textAnchor="middle"
                  fontSize="11" fontWeight="700" fill="#374151" pointerEvents="none">{total}</text>
              )}

              {/* X labels */}
              <text x={cx} y={height - pad.bottom + 16} textAnchor="middle"
                fontSize="11" fontWeight="600" fill="#374151" pointerEvents="none">{label}</text>
              <text x={cx} y={height - pad.bottom + 30} textAnchor="middle"
                fontSize="10" fill="#9ca3af" pointerEvents="none">{total} CO#</text>
            </g>
          );
        })}
      </svg>
    </>
  );
};

StackedBarChart.propTypes = {
  data: PropTypes.arrayOf(cseItemShape)
};

const CSEStatisticsChart = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="tw-bg-red-50 tw-border tw-border-red-200 tw-p-6">
        <p className="tw-text-red-600 tw-font-semibold">Error loading CSE statistics</p>
        <p className="tw-text-red-500 tw-text-sm">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <p className="tw-text-gray-500 tw-text-center tw-py-8">No data available</p>
      </Card>
    );
  }

  const totalProjects = data.reduce((s, r) => s + (r.p_count || 0) + (r.j_count || 0), 0);
  const totalCOs      = data.reduce((s, r) => s + (r.total_change_orders || 0), 0);
  const avgCOsPerProj = totalProjects > 0 ? (totalCOs / totalProjects).toFixed(2) : 0;

  return (
    <Card className="tw-bg-gradient-to-br tw-from-amber-50 tw-via-white tw-to-orange-50 tw-border tw-border-gray-200 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-p-6 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiBarChart2 className="tw-text-orange-600" size={20} />
          CSE Projects & Change Orders
        </h3>
        <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
          P number and J number project distribution by CSE head — hover for full details
        </p>
      </div>

      <div className="tw-p-6">
        <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-mb-6 tw-overflow-x-auto">
          <StackedBarChart data={data} />
        </div>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-pt-4 tw-border-t tw-border-gray-200">
          <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-blue-50 tw-to-blue-100 tw-border tw-border-blue-200">
            <p className="tw-text-xs tw-text-blue-600 tw-font-semibold tw-uppercase tw-tracking-wide">Total Projects</p>
            <p className="tw-text-3xl tw-font-bold tw-text-blue-900 tw-mt-1">{totalProjects}</p>
            <p className="tw-text-xs tw-text-blue-500 tw-mt-1">P number &amp; J number combined</p>
          </div>
          <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-green-50 tw-to-emerald-100 tw-border tw-border-green-200">
            <p className="tw-text-xs tw-text-green-600 tw-font-semibold tw-uppercase tw-tracking-wide">Total CO#</p>
            <p className="tw-text-3xl tw-font-bold tw-text-green-900 tw-mt-1">{totalCOs}</p>
            <p className="tw-text-xs tw-text-green-500 tw-mt-1">All change orders</p>
          </div>
          <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-orange-50 tw-to-orange-100 tw-border tw-border-orange-200">
            <p className="tw-text-xs tw-text-orange-600 tw-font-semibold tw-uppercase tw-tracking-wide">Avg CO# / Project</p>
            <p className="tw-text-3xl tw-font-bold tw-text-orange-900 tw-mt-1">{avgCOsPerProj}</p>
            <p className="tw-text-xs tw-text-orange-500 tw-mt-1">Across all CSEs</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

CSEStatisticsChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cse_name:             PropTypes.string,
      p_count:              PropTypes.number,
      j_count:              PropTypes.number,
      total_change_orders:  PropTypes.number,
      total_projects:       PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  error:   PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

CSEStatisticsChart.defaultProps = {
  data:    [],
  loading: false,
  error:   null
};

export default CSEStatisticsChart;
